import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  Button,
  ScrollArea,
  Input,
  Label,
  MoneyInput,
  DatePicker,
  MultiSelect,
} from "@/components/ui";

import {
  useCreateDealMutation,
  useUpdateDealMutation,
  useToast,
  useFetchLeadsQuery,
  useFetchStagesQuery
} from "@/hooks";
import { DealsValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { DealFormProps } from "@/types";
import { MessageError } from "../../message-error";

export function FormDeal({ deal, handleClose }: DealFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: createDeal, isLoading: isCreating } = useCreateDealMutation();
  const { mutateAsync: updateDeal, isLoading: isUpdating } = useUpdateDealMutation();
  const { data: leadsData, isLoading: isLoadingLeads } = useFetchLeadsQuery();
  const { data: stagesData, isLoading: isLoadingStages } = useFetchStagesQuery();
  const isLoading = isCreating || isUpdating;

  const initialValues = {
    title: deal?.title || "",
    lead_id: deal?.lead_id || [],
    value: deal?.value || 0,
    probability: deal?.probability || 0,
    expected_close_date: deal?.expected_close_date || "",
    stage_id: deal?.stage_id || "",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: DealsValidationSchema(),
    onSubmit: async (values) => {
      try {
        if (deal) {
          await updateDeal({
            id: deal.id,
            data: values
          });
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
        } else {
          await createDeal(values);
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        }
        handleClose();
        formik.resetForm();
      } catch (error) {
        toast({
          title: t("LABEL.error"),
          description: DisplayError(error, t("MESSAGE.error_message")),
          variant: "destructive",
        });
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[50vh] md:max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2"
    >
      <ScrollArea className=" h-full">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label required>Título</Label>
            <Input
              isInvalid={formik.touched.title && !!formik.errors.title}
              isValid={formik.touched.title && !formik.errors.title}
              messageError={formik.errors.title as string}
              {...formik.getFieldProps("title")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label required>Lead</Label>
              <div className="space-y-1">
                <MultiSelect
                  options={
                    leadsData?.items.map((lead) => ({
                      value: lead.id,
                      label: lead.name + (lead.company ? ` (${lead.company})` : ""),
                    })) || []
                  }
                  loading={isLoadingLeads}
                  placeholder="Selecione o lead..."
                  value={formik.values.lead_id}
                  onChange={(value) => {
                    formik.setFieldValue("lead_id", value);
                  }}
                  isInvalid={formik.touched.lead_id && !!formik.errors.lead_id}
                  isValid={formik.touched.lead_id && !formik.errors.lead_id}
                  messageError={formik.errors.lead_id as string}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label required>Estágio</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("stage_id")}
                  onValueChange={(value) => {
                    formik.setFieldValue("stage_id", value);
                  }}
                  disabled={isLoadingStages}
                >
                  <SelectTrigger
                    isInvalid={formik.touched.stage_id && !!formik.errors.stage_id}
                    isValid={formik.touched.stage_id && !formik.errors.stage_id}
                  >
                    <SelectValue placeholder="Selecione o estágio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {stagesData?.items.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded"
                              style={{ backgroundColor: stage.color || "#10b981" }}
                            />
                            {stage.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={formik.errors.stage_id as string} />
              </div>
            </div>

            <div className="space-y-2">
              <Label required>Valor</Label>
              <MoneyInput
                isInvalid={formik.touched.value && !!formik.errors.value}
                isValid={formik.touched.value && !formik.errors.value}
                messageError={formik.errors.value as string}
                {...formik.getFieldProps("value")}
              />
            </div>

            <div className="space-y-2">
              <Label>Probabilidade (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="0-100"
                isInvalid={formik.touched.probability && !!formik.errors.probability}
                isValid={formik.touched.probability && !formik.errors.probability}
                messageError={formik.errors.probability as string}
                {...formik.getFieldProps("probability")}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Data de Fechamento Prevista</Label>
              <DatePicker
                isInvalid={formik.touched.expected_close_date && !!formik.errors.expected_close_date}
                isValid={formik.touched.expected_close_date && !formik.errors.expected_close_date}
                messageError={formik.errors.expected_close_date as string}
                {...formik.getFieldProps("expected_close_date")}
              />
            </div>
          </div>
        </div>
      </ScrollArea>

      <div className="flex items-center justify-end gap-x-2">
        <Button
          type="submit"
          className="px-10"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {deal ? t("LABEL.save") : t("LABEL.create")}
        </Button>
      </div>
    </form>
  );
}